import { type ComponentType, type FC, useEffect } from "react";

import { useDesktop } from "@/hooks/use-desktop";

// Unique attribute used to scope the drag CSS rules to wrapped components.
const DRAG_ATTR = "data-desktop-drag";

// CSS rules injected once when the first withDesktopDrag component mounts.
// The [data-desktop-drag] selector scopes these rules so they don't affect
// components that manage their own drag zones (e.g. DesktopTitlebar).
const DRAG_CSS = `
[${DRAG_ATTR}] {
    -webkit-app-region: drag;
}
[${DRAG_ATTR}] button,
[${DRAG_ATTR}] a,
[${DRAG_ATTR}] input,
[${DRAG_ATTR}] select,
[${DRAG_ATTR}] textarea,
[${DRAG_ATTR}] label,
[${DRAG_ATTR}] [role="button"],
[${DRAG_ATTR}] [role="link"],
[${DRAG_ATTR}] [role="checkbox"],
[${DRAG_ATTR}] [role="radio"],
[${DRAG_ATTR}] [role="switch"],
[${DRAG_ATTR}] [role="tab"],
[${DRAG_ATTR}] [role="menuitem"],
[${DRAG_ATTR}] [role="option"],
[${DRAG_ATTR}] [role="combobox"],
[${DRAG_ATTR}] [role="slider"] {
    -webkit-app-region: no-drag;
}
`;

// Reference-counted style injection so the <style> tag is shared across
// multiple mounted instances and only removed when the last one unmounts.
let styleRef: { element: HTMLStyleElement; count: number } | null = null;

function mountStyle() {
    if (styleRef) {
        styleRef.count++;
        return;
    }
    const el = document.createElement("style");
    el.textContent = DRAG_CSS;
    document.head.appendChild(el);
    styleRef = { element: el, count: 1 };
}

function unmountStyle() {
    if (!styleRef) return;
    styleRef.count--;
    if (styleRef.count <= 0) {
        styleRef.element.remove();
        styleRef = null;
    }
}

/**
 * HOC that makes the wrapped component's background draggable in the Desktop app.
 *
 * Applies `-webkit-app-region: drag` to the wrapper so the entire area acts as
 * a window drag handle. Interactive elements (buttons, links, inputs, etc.)
 * automatically get `-webkit-app-region: no-drag` via a shared `<style>` tag,
 * so clicks on them work normally without any annotation from the wrapped component.
 *
 * Only active when running in the desktop app and not in fullscreen.
 *
 * Usage:
 *   export const LoginPage = withDesktopDrag(function LoginPage() { ... });
 */
export function withDesktopDrag<P extends object>(WrappedComponent: ComponentType<P>): FC<P> {
    const displayName = WrappedComponent.displayName || WrappedComponent.name || "Component";

    const WithDesktopDrag: FC<P> = (props) => {
        const { isDesktop, isFullscreen } = useDesktop();
        const active = isDesktop && !isFullscreen;

        useEffect(() => {
            if (!active) return;
            mountStyle();
            return unmountStyle;
        }, [active]);

        if (!active) {
            return <WrappedComponent {...props} />;
        }

        // The wrapper div needs a painted box for -webkit-app-region to work.
        // size-full ensures it fills the parent without altering the inner layout.
        return (
            <div {...{ [DRAG_ATTR]: "" }} className="size-full">
                <WrappedComponent {...props} />
            </div>
        );
    };

    WithDesktopDrag.displayName = `withDesktopDrag(${displayName})`;
    return WithDesktopDrag;
}
